import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiService } from '../services/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await apiService.forgotPassword(email);
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (err) {
      // Don't show error for security - always show success message
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-page" data-testid="forgot-password-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="login-title">Check Your Email</h1>
              <p className="login-subtitle">Password reset instructions sent</p>
            </div>

            <div className="space-y-4">
              <p className="text-[#595959] text-center text-sm">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                The link will expire in 60 minutes.
              </p>
              
              <div className="bg-[#F3F4F1] rounded-xl p-4">
                <p className="text-sm text-[#595959]">
                  <strong>Didn't receive the email?</strong>
                </p>
                <ul className="text-sm text-[#595959] mt-2 space-y-1">
                  <li>• Check your spam or junk folder</li>
                  <li>• Verify you entered the correct email</li>
                  <li>• Contact your school administrator</li>
                </ul>
              </div>

              <div className="pt-4">
                <Link to="/login">
                  <Button variant="outline" className="w-full rounded-xl border-[#E5E5E5]">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" data-testid="forgot-password-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <GraduationCap size={28} />
            </div>
            <h1 className="login-title">Reset Password</h1>
            <p className="login-subtitle">Enter your email to receive a reset link</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="forgot-password-form">
            <div className="form-group">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="pl-10 rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B] focus:ring-[#2C4C3B]/30"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="login-footer">
            <Link 
              to="/login" 
              className="inline-flex items-center text-[#2C4C3B] hover:underline text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
